import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';
import { AUTH_QUERY_KEY } from '@/auth/AuthProvider';

/**
 * Data access for the signed-in user's own profile.
 *
 * Every endpoint acts on the caller and takes no user id — that is what keeps it
 * apart from `useUsers`, which is Admin-only administration of other accounts.
 */

const BASE = '/api/profile';

export interface Profile {
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
  userName: string;
  phoneNumber: string | null;
  department: string | null;
  jobTitle: string | null;
  initials: string;
  displayName: string;
  emailConfirmed: boolean;
  phoneNumberConfirmed: boolean;
  isLockedOut: boolean;
  accessFailedCount: number;
  preferredLanguage: string;
  preferredTimezone: string;
  preferredTheme: string;
  roles: string[];
  isAdmin: boolean;
  lastLoginDate: string | null;
  lastLoginIpAddress: string | null;
  createdDate: string;
  modifiedDate: string | null;
  isActive: boolean;
}

export interface ProfileResponse {
  profile: Profile;
  /** Culture code → display name. */
  availableLanguages: Record<string, string>;
  /** Theme key → display name. */
  availableThemes: Record<string, string>;
  availableTimezones: string[];
  defaultLanguage: string;
}

export interface UpdateProfileRequest {
  firstName: string;
  lastName: string;
  phoneNumber: string | null;
  department: string | null;
  jobTitle: string | null;
}

export interface UpdatePreferencesRequest {
  preferredLanguage: string;
  preferredTimezone: string;
  preferredTheme: string;
}

export const profileKeys = {
  all: ['profile'] as const,
};

export function useProfile() {
  return useQuery<ProfileResponse>({
    queryKey: profileKeys.all,
    queryFn: () => api.get<ProfileResponse>(BASE),
  });
}

/**
 * Refreshes the profile and the session.
 *
 * The header reads the display name from `GET /api/auth/me`, which has its own
 * cache — without invalidating it, renaming yourself would leave the old name
 * in the chrome until a reload.
 */
function useInvalidateProfile() {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: profileKeys.all });
    queryClient.invalidateQueries({ queryKey: AUTH_QUERY_KEY });
  };
}

export function useUpdateProfile() {
  const invalidate = useInvalidateProfile();
  return useMutation({
    mutationFn: (request: UpdateProfileRequest) => api.put(BASE, request),
    onSuccess: invalidate,
  });
}

export function useUpdatePreferences() {
  const invalidate = useInvalidateProfile();
  return useMutation({
    mutationFn: (request: UpdatePreferencesRequest) => api.put(`${BASE}/preferences`, request),
    onSuccess: invalidate,
  });
}
